import { useCallback, useRef } from "react"

import { useMixpanel } from "../../hooks/use-mixpanel"
import { appStore } from "../../store/AppStore"
import { DomainName } from "../../utilities/constants"

import { DetailpageData, getPageFromContent } from "./DetailStore"
import { convertTypeToWebSlug } from "./use-pageview-tracking"

export const useContentTracking = ({
  eventName,
  content,
  overrideType,
  isPageTracking = true,
  pageType,
}: {
  eventName?: string
  content: DetailpageData | undefined
  overrideType?: string
  isPageTracking?: boolean
  pageType?: string
}) => {
  const track = useMixpanel()
  const slug = appStore((state) => state.slug)
  const trackOnce = useRef(true)
  const lastRouteIsBackNav = appStore((state) => state.lastRouteIsBackNav)

  return useCallback(
    async (appendProps?: Record<string, string | number | undefined>) => {
      /*don't do anything if the tracker is configured as a page tracker and already submitted
      once before. Also check if the page is loaded from back navigation
       */

      if (
        !content ||
        (isPageTracking && (!trackOnce.current || lastRouteIsBackNav))
      )
        return
      trackOnce.current = false
      const page = await getPageFromContent(content)
      const artists =
        content.__typename === "Album" ||
        content.__typename === "VodConcert" ||
        content.__typename === "Video" ||
        content.__typename === "LiveConcert"
          ? [
              ...(content.artists
                ? content.artists.edges.map((a) => ({
                    id: a.node.id,
                    name: a.node.name,
                  }))
                : []),
              ...(content.__typename !== "Video"
                ? content.groups.map((g) => ({ id: g.id, name: g.name }))
                : []),
            ]
          : []
      track(eventName ?? "content_detail_view", {
        page_type: pageType ?? slug,
        content_type: overrideType ?? content.__typename,
        content_subtype:
          content.__typename === "VodConcert" || content.__typename === "Video"
            ? content.typeDisplayName
            : content.__typename === "LiveConcert"
              ? content.liveConcertType
              : undefined,
        content_container_name: page?.title,
        content_container_id: content.id,
        content_container_artist_name: artists?.map((artist) => artist.name),
        content_container_artist_id: artists?.map((artist) => artist.id),
        content_link:
          DomainName +
          `${convertTypeToWebSlug(content.__typename)}/${content.id}`,
        content_access_type:
          page?.publicationLevel === "TICKET_REQUIRED" ? "paid" : "free",
        ...appendProps,
      })
    },
    [
      content,
      track,
      slug,
      isPageTracking,
      lastRouteIsBackNav,
      overrideType,
      pageType,
    ],
  )
}
